<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>

  <div class="page-wrapper">
    <div class="content">
      <!-- Breadcrumb -->
      <div
        class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3"
      >
        <breadcrumb :title="title" :text="text" :text1="text1" />
        <div
          class="d-flex my-xl-auto right-content align-items-center flex-wrap dq-page-actions"
        >
          <div class="d-flex align-items-center me-2 mb-2">
            <label class="me-2 mb-0 fs-12 text-muted">Frequency</label>
            <select v-model="filters.frequency" class="form-select" style="min-width: 140px">
              <option value="monthly">Monthly</option>
              <option value="weekly">Weekly</option>
              <option value="fortnightly">Fortnightly</option>
              <option value="daily">Daily</option>
            </select>
          </div>

          <!-- Period pickers -->
          <template v-if="filters.frequency === 'monthly'">
            <div class="d-flex align-items-center me-2 mb-2">
              <label class="me-2 mb-0 fs-12 text-muted">Previous</label>
              <input
                type="month"
                class="form-control"
                v-model="filters.previous_period"
              />
            </div>
            <div class="d-flex align-items-center me-2 mb-2">
              <label class="me-2 mb-0 fs-12 text-muted">Current</label>
              <input
                type="month"
                class="form-control"
                v-model="filters.current_period"
              />
            </div>
          </template>

          <template v-else>
            <div class="d-flex align-items-center flex-wrap me-2 mb-2 gap-1">
              <span class="fs-12 text-muted">Previous</span>
              <input
                v-model="filters.previous_period_start"
                type="date"
                class="form-control"
                style="min-width: 150px"
              />
              <input
                v-model="filters.previous_period_end"
                type="date"
                class="form-control"
                style="min-width: 150px"
                :disabled="filters.frequency === 'daily'"
              />
            </div>
            <div class="d-flex align-items-center flex-wrap me-2 mb-2 gap-1">
              <span class="fs-12 text-muted">Current</span>
              <input
                v-model="filters.current_period_start"
                type="date"
                class="form-control"
                style="min-width: 150px"
              />
              <input
                v-model="filters.current_period_end"
                type="date"
                class="form-control"
                style="min-width: 150px"
                :disabled="filters.frequency === 'daily'"
              />
            </div>
            <button
              type="button"
              class="btn btn-outline-secondary btn-sm mb-2 me-1"
              @click="applySuggestedWindows()"
            >
              This period
            </button>
          </template>

          <button
            class="btn btn-primary mb-2 ms-1"
            @click="fetchReport"
            :disabled="store.payrollComparisonLoading || !canCompare"
          >
            <i class="ti ti-refresh me-1"></i>Compare
          </button>

          <!-- Export -->
          <div class="mb-2 ms-2">
            <div class="dropdown">
              <a
                class="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                data-bs-toggle="dropdown"
              >
                <i class="ti ti-file-export me-1"></i>Export
              </a>
              <ul class="dropdown-menu dropdown-menu-end p-3">
                <li>
                  <a class="dropdown-item rounded-1" @click.prevent="exportCsv" v-can="'Reports.Export'">
                    <i class="ti ti-file-type-xls me-1"></i>Export as CSV
                  </a>
                </li>
                <li>
                  <a class="dropdown-item rounded-1" @click.prevent="exportPdf" v-can="'Reports.Export'">
                    <i class="ti ti-file-type-pdf me-1"></i>Export as PDF
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div class="head-icons ms-2">
            <a id="collapse-header" @click="toggleHeader">
              <i class="ti ti-chevrons-up"></i>
            </a>
          </div>
        </div>
      </div>
      <!-- /Breadcrumb -->

      <div
        v-if="filters.frequency !== 'monthly' && periodValidationMessage"
        class="alert alert-warning py-2 mb-3"
      >
        {{ periodValidationMessage }}
      </div>

      <!-- KPI Cards -->
      <div class="row mb-3 dq-stat-grid">
        <div class="col-md-4 d-flex" v-for="card in kpiCards" :key="card.key">
          <div class="card flex-fill dq-stat-card">
            <div class="card-body">
              <div
                class="d-flex align-items-center justify-content-between bg-light border rounded p-2 mb-2"
              >
                <div>
                  <span class="fs-12 text-muted mb-1 d-block">
                    {{ card.title }}
                  </span>
                  <div class="fs-12">
                    <span class="fw-semibold">{{ currency }}</span>
                    <span class="fw-semibold">
                      {{
                        Number(card.current).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                        })
                      }}
                    </span>
                    <span class="text-muted ms-1">
                      (Prev:
                      {{
                        Number(card.previous).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                        })
                      }})
                    </span>
                  </div>
                </div>
                <div class="text-end">
                  <div
                    class="badge"
                    :class="
                      card.diff > 0
                        ? 'bg-success-subtle text-success'
                        : card.diff < 0
                        ? 'bg-danger-subtle text-danger'
                        : 'bg-light text-muted'
                    "
                  >
                    <i
                      v-if="card.diff > 0"
                      class="ti ti-arrow-up-right me-1"
                    ></i>
                    <i
                      v-else-if="card.diff < 0"
                      class="ti ti-arrow-down-right me-1"
                    ></i>
                    <i v-else class="ti ti-arrow-right me-1"></i>
                    {{ formatPercent(card.percent_change) }}
                  </div>
                  <div class="fs-11 text-muted mt-1">
                    Δ:
                    {{ currency }}
                    {{
                      Number(card.diff).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                      })
                    }}
                  </div>
                </div>
              </div>
              <p class="fs-11 text-muted mb-0">
                {{ card.caption }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Table -->
      <div class="card">
        <div
          class="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3"
        >
          <h5 class="mb-0">
            Payroll Comparison
            <small class="text-muted fs-12 ms-1">
              ({{ summary?.previous_period }} vs {{ summary?.current_period }})
            </small>
          </h5>
        </div>
        <div class="card-body p-0">
          <div class="custom-datatable-filter table-responsive">
            <PayrollComparisonReportTable
              :rows="store.payrollComparisonRows"
              :loading="store.payrollComparisonLoading"
              :currency="currency"
            />
          </div>
        </div>
      </div>
    </div>

    <div
      class="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3"
    >
      <p class="mb-0">2014 - {{ new Date().getFullYear() }} &copy; DoneQuick HR & Payroll</p>
      <p>
        Powered by <a href="#" @click.prevent class="text-primary">DoneQuick Technological World</a>
      </p>
    </div>
  </div>
</template>

<style scoped>
@media (max-width: 767.98px) {
  .dq-page-actions {
    align-items: stretch !important;
  }

  .dq-page-actions > * {
    width: 100%;
    margin-left: 0 !important;
    margin-right: 0 !important;
  }

  .dq-page-actions .dropdown,
  .dq-page-actions .dropdown-toggle,
  .dq-page-actions .form-control,
  .dq-page-actions button {
    width: 100%;
  }

  .dq-stat-card .card-body {
    padding: 1rem;
  }

  .custom-datatable-filter.table-responsive {
    margin-left: -1rem;
    margin-right: -1rem;
    padding-left: 1rem;
    padding-right: 1rem;
    width: calc(100% + 2rem);
  }
}
</style>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { useEmployeeSalaryStore } from "@/stores/employeeSalaryStore";
import PayrollComparisonReportTable from "./payroll-comparison-report-table.vue";
import {
  endDateForFrequency,
  suggestPeriodWindow,
  validateFrequencyWindow,
  toYYYYMM,
} from "@/utils/payrollPeriodHelpers";

const title = "Payroll Comparison Report";
const text = "Administration";
const text1 = "Payroll Comparison";

const store = useEmployeeSalaryStore();

const filters = ref({
  frequency: "monthly",
  previous_period: "",
  current_period: "",
  previous_period_start: "",
  previous_period_end: "",
  current_period_start: "",
  current_period_end: "",
});

function toYm(d) {
  return toYYYYMM(d);
}

function initDefaultPeriods() {
  const now = new Date();

  if (filters.value.frequency === "monthly") {
    const current = new Date(now);
    const prev = new Date(now);
    prev.setMonth(prev.getMonth() - 1);
    filters.value.current_period = toYm(current);
    filters.value.previous_period = toYm(prev);
    filters.value.previous_period_start = "";
    filters.value.previous_period_end = "";
    filters.value.current_period_start = "";
    filters.value.current_period_end = "";
    return;
  }

  applySuggestedWindows(now);
}

function applySuggestedWindows(anchor = new Date()) {
  const current = suggestPeriodWindow(filters.value.frequency, anchor);
  const previousAnchor = new Date(anchor);
  const step =
    filters.value.frequency === "daily"
      ? 1
      : filters.value.frequency === "weekly"
      ? 7
      : 14;
  previousAnchor.setDate(previousAnchor.getDate() - step);
  const previous = suggestPeriodWindow(filters.value.frequency, previousAnchor);

  filters.value.current_period = "";
  filters.value.previous_period = "";
  filters.value.current_period_start = current.periodStart;
  filters.value.current_period_end = current.periodEnd;
  filters.value.previous_period_start = previous.periodStart;
  filters.value.previous_period_end = previous.periodEnd;
}

function comparisonParams() {
  const f = filters.value;
  if (f.frequency === "monthly") {
    return {
      frequency: f.frequency,
      previous_period: f.previous_period,
      current_period: f.current_period,
    };
  }

  return {
    frequency: f.frequency,
    previous_period_start: f.previous_period_start,
    previous_period_end: f.previous_period_end,
    current_period_start: f.current_period_start,
    current_period_end: f.current_period_end,
  };
}

const previousWindowValidation = computed(() =>
  validateFrequencyWindow(
    filters.value.frequency,
    filters.value.previous_period_start,
    filters.value.previous_period_end,
  ),
);

const currentWindowValidation = computed(() =>
  validateFrequencyWindow(
    filters.value.frequency,
    filters.value.current_period_start,
    filters.value.current_period_end,
  ),
);

const periodValidationMessage = computed(() => {
  if (filters.value.frequency === "monthly") return "";
  if (!previousWindowValidation.value.ok) {
    return `Previous period: ${previousWindowValidation.value.message}`;
  }
  if (!currentWindowValidation.value.ok) {
    return `Current period: ${currentWindowValidation.value.message}`;
  }
  return "";
});

const canCompare = computed(() => {
  if (filters.value.frequency === "monthly") {
    return Boolean(filters.value.previous_period && filters.value.current_period);
  }
  return (
    previousWindowValidation.value.ok &&
    currentWindowValidation.value.ok &&
    Boolean(
      filters.value.previous_period_start &&
        filters.value.previous_period_end &&
        filters.value.current_period_start &&
        filters.value.current_period_end,
    )
  );
});

function toggleHeader() {
  document.getElementById("collapse-header")?.classList.toggle("active");
  document.body.classList.toggle("header-collapse");
}

async function fetchReport() {
  if (!canCompare.value) return;
  await store.fetchPayrollComparison(comparisonParams());
}

function exportCsv() {
  store.exportPayrollComparisonReport("csv", comparisonParams());
}

function exportPdf() {
  store.exportPayrollComparisonReport("pdf", comparisonParams());
}

const summary = computed(() => store.payrollComparisonSummary || null);
const currency = computed(() => summary.value?.currency || "KES");

const kpiCards = computed(() => {
  const s = summary.value;
  if (!s) {
    return [];
  }

  const gross = s.totals?.gross || {};
  const net = s.totals?.net || {};
  const ded = s.totals?.deductions || {};

  return [
    {
      key: "gross",
      title: "Gross Payroll",
      previous: gross.previous || 0,
      current: gross.current || 0,
      diff: gross.diff || 0,
      percent_change: gross.percent_change ?? null,
      caption: "Total gross payroll difference between selected periods.",
    },
    {
      key: "net",
      title: "Net Payroll",
      previous: net.previous || 0,
      current: net.current || 0,
      diff: net.diff || 0,
      percent_change: net.percent_change ?? null,
      caption: "What you actually paid out to employees.",
    },
    {
      key: "deductions",
      title: "Total Deductions",
      previous: ded.previous || 0,
      current: ded.current || 0,
      diff: ded.diff || 0,
      percent_change: ded.percent_change ?? null,
      caption: "Combined statutory & other deductions movement.",
    },
  ];
});

function formatPercent(v) {
  if (v === null || v === undefined) return "-";
  return `${Number(v).toFixed(2)}%`;
}

watch(
  () => filters.value.frequency,
  () => {
    initDefaultPeriods();
  },
);

watch(
  () => filters.value.previous_period_start,
  (start) => {
    if (filters.value.frequency === "monthly" || !start) return;
    filters.value.previous_period_end = endDateForFrequency(
      filters.value.frequency,
      start,
    );
  },
);

watch(
  () => filters.value.current_period_start,
  (start) => {
    if (filters.value.frequency === "monthly" || !start) return;
    filters.value.current_period_end = endDateForFrequency(
      filters.value.frequency,
      start,
    );
  },
);

onMounted(() => {
  initDefaultPeriods();
  fetchReport();
});
</script>
